import React, { useState } from 'react';
import { Table, Badge } from 'react-bootstrap';
import BookCover from '../../components/BookCover/BookCover';
import './SearchMain.css';

const config = require('../../config.json');

export default function OffcanvasTemplate(props) {
  const { selectedBook } = props;
  const [copied, setCopied] = useState(false);

  if (!selectedBook) return null;

  const isArticle = !!selectedBook.articleID || (!!selectedBook.articleTitle && !selectedBook.bookPrice && !!selectedBook.articleAuthor);

  if (isArticle) {
    const callNumber = selectedBook.callNo || selectedBook.journalCallNo || `${selectedBook.bookClassificationNo || ''} ${selectedBook.authorMark || ''}`.trim();
    const hostTitle = selectedBook.journalTitle || selectedBook.bookTitle || '';

    const handleCopyCallNo = () => {
      const textToCopy = callNumber || selectedBook.accessionNo?.toString() || '';
      if (textToCopy && navigator.clipboard) {
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };

    return (
      <div className="p-2">
        {/* Article Icon Header & Title */}
        <div className="text-center mb-3">
          <div
            className="d-inline-flex justify-content-center align-items-center mb-2"
            style={{
              width: '68px',
              height: '68px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #4338ca 100%)',
              color: '#ffffff',
              fontSize: '28px',
              boxShadow: '0 6px 18px rgba(30, 27, 75, 0.25)',
            }}
          >
            <i className="bi bi-file-earmark-text"></i>
          </div>
          <div className="fw-bold fs-6 text-dark">{selectedBook.articleTitle || 'Untitled Article'}</div>
          {selectedBook.articleSubTitle && selectedBook.articleSubTitle !== 'NA' && (
            <div className="text-muted small mb-1 fst-italic">{selectedBook.articleSubTitle}</div>
          )}
          <div className="text-secondary small">
            <i className="bi bi-person me-1"></i>{selectedBook.articleAuthor || 'Unknown Author'}
          </div>
        </div>

        {/* Shelf Location & Call Number Card */}
        <div className="vj-shelf-location-card">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="small fw-bold text-muted text-uppercase">
              <i className="bi bi-geo-alt-fill text-danger me-1"></i> Shelf Location & Call No
            </span>
            <Badge bg={selectedBook.articleStatus === 'Active' || !selectedBook.articleStatus ? 'success' : 'secondary'}>
              {selectedBook.articleStatus || 'Active'}
            </Badge>
          </div>
          <div className="d-flex justify-content-between align-items-center my-1">
            <div className="vj-shelf-callno">
              Call No: {callNumber || 'Periodicals Section'}
            </div>
            {callNumber && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary py-0 px-2 d-flex align-items-center gap-1"
                onClick={handleCopyCallNo}
                title="Copy Call Number"
                style={{ fontSize: '0.78rem', borderRadius: '6px' }}
              >
                <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'}`}></i>
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
          <div className="small text-muted">
            Host {selectedBook.journalTitle ? 'Journal' : 'Book'}: <strong>{hostTitle || 'Library Catalogue'}</strong>
            {selectedBook.volissuesNo ? ` • Vol. ${selectedBook.volissuesNo}` : ''}
            {selectedBook.accessionNo ? ` • Acc #: #${selectedBook.accessionNo}` : ''}
          </div>
        </div>

        {/* Bibliographic Details Table */}
        <div className="border rounded bg-white overflow-hidden shadow-sm">
          <Table hover size="sm" className="mb-0 align-middle">
            <tbody>
              <tr>
                <th className="bg-light text-muted small ps-3" style={{ width: '38%' }}>Article ID</th>
                <td className="font-monospace text-primary fw-semibold">{selectedBook.articleID || `#${selectedBook.accessionNo}`}</td>
              </tr>
              <tr>
                <th className="bg-light text-muted small ps-3">Article Title</th>
                <td className="fw-semibold">{selectedBook.articleTitle}</td>
              </tr>
              {selectedBook.articleSubTitle && selectedBook.articleSubTitle !== 'NA' && (
                <tr>
                  <th className="bg-light text-muted small ps-3">Subtitle</th>
                  <td className="fst-italic">{selectedBook.articleSubTitle}</td>
                </tr>
              )}
              <tr>
                <th className="bg-light text-muted small ps-3">Author(s)</th>
                <td>{selectedBook.articleAuthor || 'Unknown'}</td>
              </tr>
              {hostTitle && (
                <tr>
                  <th className="bg-light text-muted small ps-3">Host Journal / Book</th>
                  <td className="fw-bold text-primary">{hostTitle}</td>
                </tr>
              )}
              {callNumber && (
                <tr>
                  <th className="bg-light text-muted small ps-3">Call Number</th>
                  <td className="font-monospace text-primary fw-semibold">{callNumber}</td>
                </tr>
              )}
              {selectedBook.volissuesNo && (
                <tr>
                  <th className="bg-light text-muted small ps-3">Volume / Issue</th>
                  <td>{selectedBook.volissuesNo}</td>
                </tr>
              )}
              {selectedBook.articleKeywords && (
                <tr>
                  <th className="bg-light text-muted small ps-3">Keywords</th>
                  <td>{selectedBook.articleKeywords}</td>
                </tr>
              )}
              {selectedBook.articleDescription && (
                <tr>
                  <th className="bg-light text-muted small ps-3">Abstract</th>
                  <td className="small text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedBook.articleDescription}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Student Shelf Tip */}
        <div className="mt-3 p-2 bg-light rounded small text-muted text-center border">
          <i className="bi bi-info-circle text-primary me-1"></i>
          To read this chapter/article, locate host book <strong>[Acc #{selectedBook.accessionNo}] {selectedBook.bookTitle}</strong> on the shelf.
        </div>
      </div>
    );
  }

  const callNumber = `${selectedBook.bookClassificationNo || ''} ${selectedBook.authorMark || ''}`.trim();
  const posterUrl = `${config.s3.bookPoster}${selectedBook.accessionNo}.jpeg`;
  const isBoundJournal = (selectedBook.bookAuthor || '').trim().toLowerCase() === 'bound journal';

  const handleCopyCallNo = () => {
    const textToCopy = callNumber || selectedBook.accessionNo?.toString() || '';
    if (textToCopy && navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="p-2">
      {/* Cover Image & Quick ID */}
      <div className="text-center mb-3">
        <div className="d-flex justify-content-center mb-2">
          <BookCover
            title={selectedBook.bookTitle}
            author={selectedBook.bookAuthor}
            edition={selectedBook.bookEdition}
            category={selectedBook.bookCategory}
            callNumber={selectedBook.bookClassificationNo}
            accessionNo={selectedBook.accessionNo}
            imageUrl={posterUrl}
            size="lg"
            style={{ width: '130px', height: '180px' }}
          />
        </div>
        <div className="fw-bold fs-6 text-dark">{selectedBook.bookTitle}</div>
        {selectedBook.bookSubTitle && selectedBook.bookSubTitle !== 'NA' && (
          <div className="text-muted small mb-1">{selectedBook.bookSubTitle}</div>
        )}
        {isBoundJournal && (
          <div className="mt-2">
            <span className="badge-bound-journal">
              <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
            </span>
          </div>
        )}
      </div>

      {/* Shelf Location & Call Number Card */}
      <div className="vj-shelf-location-card">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="small fw-bold text-muted text-uppercase">
            <i className="bi bi-geo-alt-fill text-danger me-1"></i> Library Shelf Location
          </span>
          <div className="d-flex align-items-center gap-1">
            {isBoundJournal && (
              <span className="badge-bound-journal" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>
                <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
              </span>
            )}
            <Badge bg={selectedBook.bookStatus === 'Active' || selectedBook.bookStatus === 'Available' ? 'success' : 'warning'}>
              {selectedBook.bookStatus === 'Active' ? 'Available on Shelf' : selectedBook.bookStatus || 'Available'}
            </Badge>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center my-1">
          <div className="vj-shelf-callno">
            Call No: {callNumber || 'N/A'}
          </div>
          {callNumber && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary py-0 px-2 d-flex align-items-center gap-1"
              onClick={handleCopyCallNo}
              title="Copy Call Number"
              style={{ fontSize: '0.78rem', borderRadius: '6px' }}
            >
              <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'}`}></i>
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
        <div className="small text-muted">
          Accession #: <strong>#{selectedBook.accessionNo}</strong> &bull; Scope: <strong>{selectedBook.bookScope || 'Reference'}</strong>
          {isBoundJournal && (
            <span> &bull; Section: <strong>{selectedBook.bookLocation || 'Bound Journal Section'}</strong></span>
          )}
        </div>
      </div>

      {/* Bibliographic Details Table */}
      <div className="border rounded bg-white overflow-hidden shadow-sm">
        <Table hover size="sm" className="mb-0 align-middle">
          <tbody>
            <tr>
              <th className="bg-light text-muted small ps-3" style={{ width: '38%' }}>Author / Type</th>
              <td className="fw-semibold">
                {isBoundJournal ? (
                  <span className="badge-bound-journal">
                    <i className="bi bi-journal-bookmark-fill"></i> Bound Journal
                  </span>
                ) : (
                  selectedBook.bookAuthor || 'Unknown'
                )}
              </td>
            </tr>
            <tr>
              <th className="bg-light text-muted small ps-3">Category</th>
              <td>{selectedBook.bookCategory || (isBoundJournal ? 'Periodical / Journal' : 'Theology')}</td>
            </tr>
            {selectedBook.bookPublisherName && (
              <tr>
                <th className="bg-light text-muted small ps-3">Publisher</th>
                <td>{selectedBook.bookPublisherName}</td>
              </tr>
            )}
            {selectedBook.bookPublishYear && (
              <tr>
                <th className="bg-light text-muted small ps-3">Publish Year</th>
                <td>{selectedBook.bookPublishYear}</td>
              </tr>
            )}
            {selectedBook.bookEdition && selectedBook.bookEdition !== 'NA' && (
              <tr>
                <th className="bg-light text-muted small ps-3">Edition</th>
                <td>{selectedBook.bookEdition}</td>
              </tr>
            )}
            {selectedBook.bookPages && selectedBook.bookPages !== '0' && (
              <tr>
                <th className="bg-light text-muted small ps-3">Pages</th>
                <td>{selectedBook.bookPages}</td>
              </tr>
            )}
            {selectedBook.isbn && (
              <tr>
                <th className="bg-light text-muted small ps-3">{isBoundJournal ? 'ISSN' : 'ISBN'}</th>
                <td>{selectedBook.isbn}</td>
              </tr>
            )}
            {selectedBook.bookLocation && (
              <tr>
                <th className="bg-light text-muted small ps-3">Location / Section</th>
                <td>{selectedBook.bookLocation}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Student Shelf Tip */}
      <div className="mt-3 p-2 bg-light rounded small text-muted text-center border">
        <i className="bi bi-info-circle text-primary me-1"></i>
        {isBoundJournal ? (
          <>
            This volume is an archived <strong>Bound Journal</strong>.
            Locate it in the Serials / Bound Journal section using <strong>Call Number: {callNumber || selectedBook.accessionNo}</strong>.
          </>
        ) : (
          <>
            Note down the <strong>Call Number ({callNumber || selectedBook.accessionNo})</strong> to locate this book on the stacks.
          </>
        )}
      </div>
    </div>
  );
}
